import { useEffect, useRef, useState } from 'react';
import type { Preview, PreviewRequest } from '../api/generated/types.gen';

/** One read-only calculation at a time. New books coalesce; changed terms cancel old work. */
export function useCostPreview(request: PreviewRequest | null, bookRevision: number | null) {
  // Every term that changes the answer is part of the key, so a buy estimate never stands in for a sale.
  const context = request ? JSON.stringify([request.ticker, request.side, request.quantity, request.price_cap, request.action ?? 'buy',
    request.position_quantity ?? null, request.position_cost ?? null]) : null;
  const key = context == null ? null : `${context}:${bookRevision}`;
  const latest = useRef({ request, context, key });
  latest.current = { request, context, key };
  const wake = useRef<(() => void) | null>(null);
  const [result, setResult] = useState<{ context: string; key: string; value: Preview } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setResult(null); setError(''); setBusy(false);
    if (context == null) { wake.current = null; return; }
    let stopped = false, running = false, failures = 0;
    let completed: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    const schedule = (delay: number) => {
      if (!stopped && !running && timer == null) timer = setTimeout(() => { timer = undefined; void run(); }, delay);
    };
    const run = async () => {
      const demand = latest.current;
      if (stopped || running || !demand.request || !demand.key || demand.context !== context || demand.key === completed) return;
      running = true; setBusy(true); setError('');
      controller = new AbortController();
      const timeout = setTimeout(() => controller?.abort(), 4000);
      let retryDelay: number | null = null;
      try {
        const response = await fetch('/api/v1/previews', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(demand.request), signal: controller.signal,
        });
        if (!response.ok) throw new Error('Cost estimate unavailable. Retrying current depth.');
        const value = await response.json() as Preview;
        if (!stopped) {
          completed = demand.key; failures = 0;
          setResult({ context, key: demand.key, value });
        }
      } catch {
        if (!stopped) {
          setResult(null);
          setError('Cost estimate unavailable. Retrying current depth.');
          retryDelay = Math.min(10000, 1000 * 2 ** Math.min(failures++, 4));
        }
      } finally {
        clearTimeout(timeout);
        running = false;
        if (!stopped) {
          setBusy(false);
          if (retryDelay != null) schedule(retryDelay);
          else if (latest.current.key !== completed) schedule(0);
        }
      }
    };
    wake.current = () => schedule(200);
    schedule(200);
    return () => { stopped = true; wake.current = null; clearTimeout(timer); controller?.abort(); };
  }, [context]);

  useEffect(() => { wake.current?.(); }, [key]);
  const current = context != null && result?.context === context ? result : null;
  return { preview: current?.value ?? null, updating: busy || !!(current && current.key !== key), error: context == null ? '' : error };
}
